/**
 * localStorage-backed isolated filters for the Monthly Reports page.
 *
 * Stores Site and Department (single-select) only. Never shared with or
 * affected by the multiselect shared locationFiltersSession bucket.
 *
 * REBUILD-393 / JW 392
 */

export interface MonthlyReportFilters {
  siteId: number | null
  departmentId: number | null
}

export const MONTHLY_REPORT_FILTERS_STORAGE_PREFIX = 'ell.monthlyReportFilters.v1'

export const EMPTY_MONTHLY_REPORT_FILTERS: MonthlyReportFilters = {
  siteId: null,
  departmentId: null,
}

export const monthlyReportFiltersStorageKey = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): string =>
  `${MONTHLY_REPORT_FILTERS_STORAGE_PREFIX}:tenant:${tenantId ?? 'unknown'}:user:${userId ?? 'unknown'}`

export const readMonthlyReportFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): MonthlyReportFilters => {
  if (tenantId == null || typeof localStorage === 'undefined') {
    return { ...EMPTY_MONTHLY_REPORT_FILTERS }
  }

  try {
    const raw = localStorage.getItem(monthlyReportFiltersStorageKey(tenantId, userId))
    if (!raw) return { ...EMPTY_MONTHLY_REPORT_FILTERS }

    const parsed = JSON.parse(raw) as Partial<MonthlyReportFilters>
    const siteId =
      typeof parsed.siteId === 'number' && Number.isInteger(parsed.siteId) && parsed.siteId > 0
        ? parsed.siteId
        : null
    const departmentId =
      typeof parsed.departmentId === 'number' &&
      Number.isInteger(parsed.departmentId) &&
      parsed.departmentId > 0
        ? parsed.departmentId
        : null
    return { siteId, departmentId }
  } catch {
    return { ...EMPTY_MONTHLY_REPORT_FILTERS }
  }
}

export const writeMonthlyReportFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
  filters: MonthlyReportFilters,
): void => {
  if (tenantId == null || typeof localStorage === 'undefined') return

  try {
    localStorage.setItem(monthlyReportFiltersStorageKey(tenantId, userId), JSON.stringify(filters))
  } catch {
    // QuotaExceededError / SecurityError — persistence is best-effort.
  }
}

export const clearMonthlyReportFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): void => {
  if (tenantId == null || typeof localStorage === 'undefined') return
  localStorage.removeItem(monthlyReportFiltersStorageKey(tenantId, userId))
}
